import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    FileUp, BarChart3, Network, Shield, ChevronDown, ChevronUp,
    ArrowRight, Sparkles, FlaskConical, TrendingUp, Download, Eye
} from "lucide-react";
import Logo from "../images/Logo.png";
import NetworkCanvas from "./NetworkCanvas";

const features = [
    {
        icon: <BarChart3 size={22} />,
        title: "Three-Dimensional Scoring",
        desc: "Every manuscript is evaluated across Novelty, Methodology, and Impact — giving you a complete picture of your research's strengths.",
    },
    {
        icon: <Network size={22} />,
        title: "Graph Network Analysis",
        desc: "Your paper is placed within a network of 250,000+ publications. Scores are derived from network topology.",
    },
    {
        icon: <Shield size={22} />,
        title: "Privacy First",
        desc: "Your manuscripts are never stored, shared, or used for training. You control your data and can delete it at any time.",
    },
    {
        icon: <Download size={22} />,
        title: "Exportable Reports",
        desc: "Download shareable PDF reports summarizing your manuscript's analysis, scores, and positioning within the field.",
    },
    {
        icon: <Eye size={22} />,
        title: "Similar Papers Discovery",
        desc: "Discover related research through network-based similarity — find papers with overlapping contributions you may have missed.",
    },
    {
        icon: <FlaskConical size={22} />,
        title: "Validated Accuracy",
        desc: "90% accuracy (AUC) validated against expert evaluations of 47 annotated manuscripts using stratified k-fold cross-validation.",
    },
];

const scores = [
    {
        icon: <Sparkles size={26} />,
        label: "Novelty",
        value: "72",
        color: "#5ecfa0",
        desc: "Measures originality — how well your work bridges disconnected research areas and introduces rare concept combinations.",
    },
    {
        icon: <FlaskConical size={26} />,
        label: "Methodology",
        value: "68",
        color: "var(--series-blue)",
        desc: "Assesses rigor — experimental design quality, reproducibility, and how well methods bridge across research areas.",
    },
    {
        icon: <TrendingUp size={26} />,
        label: "Impact",
        value: "81",
        color: "var(--bronze)",
        desc: "Evaluates influence — your paper's potential reach and significance within the broader scientific network.",
    },
];

const steps = [
    { num: "1", title: "Upload Your Manuscript", desc: "Drag and drop your PDF — we support research articles and preprints in microbiology and virology." },
    { num: "2", title: "AI Analyzes Your Paper", desc: "Your manuscript is placed in a heterogeneous graph network and evaluated across hundreds of structural features." },
    { num: "3", title: "Get Your Scores", desc: "Receive calibrated Novelty, Methodology, and Impact scores with benchmarking against field averages." },
];

const displayedFaqs = [
    {
        question: "What is DeepThought?",
        answer: "DeepThought is a tool that embeds a written work into a heterogeneous knowledge graph built from thousands of scientific publications. Its structural position in this graph is used both to identify related research and potential collaborators, and to estimate the work\u2019s novelty, methodological rigor, and potential impact.",
    },
    {
        question: "How does the scoring system work?",
        answer: "Each uploaded manuscript is represented as a node in a large heterogeneous graph derived from tens of thousands of papers. The system computes how that node connects to other papers and concept nodes using graph\u2011theoretic features and learned graph embeddings. A set of machine\u2011learning models then maps these features to calibrated scores for Novelty, Methods, and Impact.",
    },
    {
        question: "How accurate are the scores?",
        answer: "The system was validated using stratified cross\u2011validation and bootstrap analysis on an expert\u2011labeled set. It achieved F1 scores of roughly 0.87 for Methods, 0.87 for Novelty, and 0.90 for Impact, with Impact AUC around 0.90 for distinguishing high\u2011 vs low\u2011impact publications.",
    },
    {
        question: "Is my manuscript stored or shared?",
        answer: "Manuscripts are processed privately and securely. You control whether your files remain stored for future reference. We do not share, sell, or publish manuscripts, and we do not expose your content to other users. Uploaded manuscripts are not added to training datasets.",
    },
    {
        question: "What types of papers can I upload?",
        answer: "DeepThought currently supports research articles, preprints, and manuscripts in microbiology and virology and closely related domains. The system is optimized for documents within these fields; papers outside these domains may receive less stable results.",
    },
    {
        question: "How should scientists use these scores?",
        answer: "Scores are decision\u2011support tools, not verdicts. They help authors understand methodological strength, conceptual originality, and potential influence, but they do not replace expert evaluation, peer review, or domain\u2011specific judgment.",
    },
];

export default function Landing() {
    const navigate = useNavigate();
    const [openFaq, setOpenFaq] = useState(null);

    return (
        <div className="landing-page">
            {/* ─── Nav ──────────────────────────────────── */}
            <nav className="landing-nav">
                <div className="landing-nav-inner">
                    <div className="landing-nav-brand" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}>
                        <img src={Logo} alt="DeepThought" className="landing-nav-logo theme-invert" />
                        <span className="landing-nav-name">DeepThought</span>
                    </div>
                    <div className="landing-nav-links">
                        <a href="#features" className="landing-nav-link">Features</a>
                        <a href="#how-it-works" className="landing-nav-link">How It Works</a>
                        <button className="landing-nav-link" onClick={() => navigate("/about")}>About Us</button>
                        <button className="landing-nav-link" onClick={() => navigate("/faq")}>FAQ</button>
                    </div>
                    <div className="landing-nav-actions">
                        <button className="landing-nav-login" onClick={() => navigate("/login")}>Log In</button>
                        <button className="landing-nav-cta" onClick={() => navigate("/register")}>
                            Get Started <ArrowRight size={14} />
                        </button>
                    </div>
                </div>
            </nav>

            {/* ─── Hero ─────────────────────────────────── */}
            <section className="landing-hero">
                <NetworkCanvas />
                <div className="landing-hero-content">
                    <div className="landing-hero-badge">AI-Powered Research Analytics</div>
                    <h1 className="landing-hero-title">
                        Understand your manuscript's<br />place in science
                    </h1>
                    <p className="landing-hero-sub">
                        DeepThought evaluates your research across Novelty, Methodology, and Impact
                        using graph network analysis of 250,000+ publications.
                    </p>
                    <div className="landing-hero-actions">
                        <button className="landing-hero-btn" onClick={() => navigate("/register")}>
                            Start Evaluating <ArrowRight size={16} />
                        </button>
                        <button className="landing-hero-btn-outline" onClick={() => navigate("/sample/submission")}>
                            View Sample Report
                        </button>
                    </div>
                    <p className="landing-hero-trust">
                        Free to use &middot; No credit card required &middot; Your data stays private
                    </p>
                </div>
            </section>

            {/* ─── Explainer Video ──────────────────────── */}
            <section className="landing-video">
                <div className="landing-section-inner">
                    <p className="landing-section-label">See It In Action</p>
                    <h2 className="landing-section-title">How DeepThought works</h2>
                    <div className="landing-video-wrap">
                        <iframe
                            src="https://www.youtube.com/embed/BRvO5RtEmqI"
                            title="DeepThought Explainer"
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </div>
                </div>
            </section>

            {/* ─── Score Preview ─────────────────────────── */}
            <section className="landing-scores">
                <div className="landing-section-inner">
                    <p className="landing-section-label">What You'll Get</p>
                    <h2 className="landing-section-title">Three scores. One complete picture.</h2>
                    <p className="landing-section-sub">
                        Each manuscript is scored across three dimensions that together capture its scientific value — derived from where it sits in the research landscape, not from who cites it.
                    </p>
                    <div className="landing-scores-grid">
                        {scores.map((s, i) => (
                            <div className="landing-score-card" key={i}>
                                <div className="landing-score-icon" style={{ color: s.color }}>{s.icon}</div>
                                <div className="landing-score-value" style={{ color: s.color }}>{s.value}</div>
                                <div className="landing-score-label">{s.label}</div>
                                <p className="landing-score-desc">{s.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── Features ──────────────────────────────── */}
            <section className="landing-features" id="features">
                <div className="landing-section-inner">
                    <p className="landing-section-label">Why DeepThought</p>
                    <h2 className="landing-section-title">Built for researchers, by researchers</h2>
                    <p className="landing-section-sub">
                        A decision-support tool designed to give you fast, transparent, data-driven insights — not replace peer review.
                    </p>
                    <div className="landing-features-grid">
                        {features.map((f, i) => (
                            <div className="landing-feature-card" key={i}>
                                <div className="landing-feature-icon">{f.icon}</div>
                                <h3 className="landing-feature-title">{f.title}</h3>
                                <p className="landing-feature-desc">{f.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── How It Works ──────────────────────────── */}
            <section className="landing-how" id="how-it-works">
                <div className="landing-section-inner">
                    <p className="landing-section-label">How It Works</p>
                    <h2 className="landing-section-title">From upload to insight in minutes</h2>
                    <div className="landing-steps">
                        {steps.map((s, i) => (
                            <div className="landing-step" key={i}>
                                <div className="landing-step-num">{s.num}</div>
                                <div className="landing-step-body">
                                    <h3 className="landing-step-title">{s.title}</h3>
                                    <p className="landing-step-desc">{s.desc}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ─── About / Mission ───────────────────────── */}
            <section className="landing-about">
                <div className="landing-section-inner">
                    <div className="landing-about-card">
                        <NetworkCanvas dark />
                        <div className="landing-about-content">
                            <p className="landing-section-label" style={{ color: "#5ecfa0" }}>About 42Degrees</p>
                            <h2 className="landing-about-title">A new model for evaluating science</h2>
                            <p className="landing-about-text">
                                DeepThought builds a network of related publications using semantic embeddings, then evaluates where your manuscript sits in that network's topology. Scores are derived from patterns in the network — distance from dense clusters, cross-cluster connections, and centrality — not from citation counts or popularity.
                            </p>
                            <p className="landing-about-text">
                                Part of the 42Degrees publishing ecosystem, DeepThought advances fair, timely assessment that celebrates creativity and impact in scientific research.
                            </p>
                            <a className="landing-wp-link" href="/whitepaper.pdf" target="_blank" rel="noopener noreferrer">
                                Read the White Paper <ArrowRight size={14} />
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── FAQ ───────────────────────────────────── */}
            <section className="landing-faq" id="faq">
                <div className="landing-section-inner">
                    <p className="landing-section-label">FAQ</p>
                    <h2 className="landing-section-title">Common questions</h2>
                    <div className="landing-faq-list">
                        {displayedFaqs.map((faq, i) => (
                            <div className={"landing-faq-item" + (openFaq === i ? " landing-faq-open" : "")} key={i}>
                                <button className="landing-faq-q" onClick={() => setOpenFaq(openFaq === i ? null : i)}>
                                    <span>{faq.question}</span>
                                    {openFaq === i ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                                </button>
                                {openFaq === i && (
                                    <div className="landing-faq-a">{faq.answer}</div>
                                )}
                            </div>
                        ))}
                    </div>
                    <button className="landing-faq-viewall" onClick={() => navigate("/faq")}>
                        View all FAQs <ArrowRight size={14} />
                    </button>
                </div>
            </section>

            {/* ─── CTA ───────────────────────────────────── */}
            <section className="landing-cta">
                <div className="landing-section-inner">
                    <div className="landing-cta-card">
                        <NetworkCanvas />
                        <div className="landing-cta-content">
                            <h2 className="landing-cta-title">Ready to evaluate your manuscript?</h2>
                            <p className="landing-cta-sub">
                                Join researchers using DeepThought to understand how their work fits within the broader scientific landscape.
                            </p>
                            <button className="landing-hero-btn" onClick={() => navigate("/register")} style={{ margin: "0 auto" }}>
                                Create Free Account <ArrowRight size={16} />
                            </button>
                        </div>
                    </div>
                </div>
            </section>

            {/* ─── Footer ────────────────────────────────── */}
            <footer className="landing-footer">
                <div className="landing-footer-inner">
                    <div className="landing-footer-brand">
                        <img src={Logo} alt="DeepThought" className="landing-nav-logo" />
                        <span className="landing-nav-name">DeepThought</span>
                    </div>
                    <p className="landing-footer-copy">
                        &copy; {new Date().getFullYear()} 42Degrees. All rights reserved.
                    </p>
                    <div className="landing-footer-links">
                        <button className="landing-footer-link" onClick={() => navigate("/about")}>About Us</button>
                        <a href="/whitepaper.pdf" target="_blank" rel="noopener noreferrer" className="landing-footer-link">White Paper</a>
                        <button className="landing-footer-link" onClick={() => navigate("/faq")}>FAQ</button>
                        <button className="landing-footer-link" onClick={() => navigate("/terms")}>Terms</button>
                        <button className="landing-footer-link" onClick={() => navigate("/privacy")}>Privacy</button>
                    </div>
                </div>
            </footer>
        </div>
    );
}
